import React, { useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout.tsx';
import { useNavigate } from 'react-router';
import { baseurly, X_API_KEY } from '../../utils/apis.ts';
import { Incident } from '../../Interfaces/types.ts';
import { IncidentResponse } from '../../Interfaces/types.ts';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

const getInitials = (name: string) => {
  if (!name) return '';
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const IncidentLanding: React.FC = () => {
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState<IncidentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchIncidents = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${baseurly}/issues`, {
          headers: { 'X-API-KEY': X_API_KEY },
        });
        if (!res.ok) throw new Error('Failed to fetch incidents');
        const data = await res.json();
        setIncidents(Array.isArray(data) ? data : []);
        console.log('Fetched incidents:', data);
      } catch (err: any) {
        setError('Could not load incidents.');
      } finally {
        setLoading(false);
      }
    };
    fetchIncidents();
  }, []);

  return (
    <MainLayout>
      <div className="min-h-screen bg-blue-900 pb-32">
        {/* Page Title */}
        <div className="px-4 sm:px-6 pt-6">
          <h1 className="text-white text-lg font-medium">Equipment Incidents</h1>
        </div>

        {/* White card */}
        <div className="mt-4 bg-white rounded-t-3xl pb-10">
          {/* Sub-header with title + image */}
          <div className="bg-white border rounded-3xl p-4 sm:p-8 mx-4 sm:mx-6 flex flex-col sm:flex-row justify-between items-center">
            <div>
              <h2 className="font-bold text-blue-900 text-lg mb-2 sm:mb-0">
                Equipment Incident/Accident Form
              </h2>
            </div>
            <img
              src={digitalOceanImageUrl("products/kayoola_coach/images/orders-coach/carousel.png")}
              alt="Equipment"
              className="w-32 h-auto"
            />
          </div>

          {/* Reported Incidents header and button */}
          <div className="flex items-center justify-between mt-6 mx-4 sm:mx-6">
            <span className="font-semibold text-blue-900">Reported Incidents</span>
            <button
              onClick={() => navigate('/incidentReport-01')}
              className="bg-blue-900 text-white font-semibold py-2 px-4 rounded-full shadow hover:bg-blue-800 transition duration-200 text-sm"
            >
              Report an Incident
            </button>
          </div>

          {/* Incident List or Empty State */}
          <div className="mt-4 mx-4 sm:mx-6 space-y-3">
            {loading ? (
              <div className="text-center text-blue-900 py-8">Loading...</div>
            ) : error ? (
              <div className="text-center text-red-500 py-8">{error}</div>
            ) : incidents.length === 0 ? (
              <div className="text-center text-blue-900 py-8">No incidents reported yet.</div>
            ) : (
              incidents.map((incident, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 bg-blue-100 rounded-lg px-4 py-3 shadow-sm relative cursor-pointer hover:bg-blue-200"
                  onClick={() => navigate(`/incident-detail/${idx}`, { state: { incident } })}
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-lg" style={{ background: '#0B3C82' }}>
                    {getInitials(incident.operator || incident.equipment || 'IN')}
                  </div>
                  {/* Details */}
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-blue-900">
                      Equipment Incident: <span className="font-normal">{incident.equipment || 'N/A'}</span>
                    </div>
                    <div className="text-xs text-gray-700 truncate max-w-xs">
                      {incident.description || 'No description provided.'}
                    </div>
                  </div>
                  {/* Menu icon */}
                  <button className="absolute right-3 top-3 text-gray-600 hover:text-blue-900" onClick={e => e.stopPropagation()}>
                    <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="4" r="1.5"/><circle cx="10" cy="10" r="1.5"/><circle cx="10" cy="16" r="1.5"/></svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        <ClientBottomNav />
      </div>
    </MainLayout>
  );
};

export default IncidentLanding;